import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  Clock,
  AlertCircle,
  TrendingUp,
  Calendar,
  User,
  FolderOpen,
  CheckCircle
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import timesheetService from '../../services/timesheetService'
import RevisionHistoryModal from '../../components/RevisionHistoryModal/RevisionHistoryModal'
import '../../components/RevisionHistoryModal/RevisionHistoryModal.css'
import '../../components/ConfirmationModal/ConfirmationModal.css'

const TimesheetList = () => {
  const navigate = useNavigate()
  const [selectedRows, setSelectedRows] = useState([])
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalTimesheets: 0,
    totalHours: 0,
    draft: 0,
    submitted: 0,
    approved: 0,
    rejected: 0,
    invoiced: 0
  })
  const [showRevisionHistory, setShowRevisionHistory] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const [selectedTimesheetData, setSelectedTimesheetData] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [timesheetsResponse, statsResponse] = await Promise.all([
        timesheetService.getAllTimesheets(),
        timesheetService.getTimesheetStats()
      ])

      if (timesheetsResponse.success !== false && timesheetsResponse.timesheets) {
        setData(timesheetsResponse.timesheets || [])
        
        // Show toast if no data
        if ((timesheetsResponse.timesheets || []).length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No timesheets found. Create your first timesheet to get started!')
        }
      } else {
        toast.error(timesheetsResponse.message || 'Failed to load timesheets')
      }

      if (statsResponse.success) {
        setStats(statsResponse.data || {})
      }
    } catch (error) {
      console.error('Error loading data:', error)
      if (!error._errorHandled) {
        toast.error('Failed to load timesheets data')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleAddNew = () => {
    navigate('/timesheets/new')
  }

  const handleEdit = (timesheet) => {
    const timesheetId = timesheet._id || timesheet.id
    if (!timesheetId) {
      toast.error('Timesheet ID not found')
      return
    }
    navigate(`/timesheets/edit/${timesheetId}`)
  }

  const handleView = (timesheet) => {
    const timesheetId = timesheet._id || timesheet.id
    if (!timesheetId) {
      toast.error('Timesheet ID not found')
      return
    }
    navigate(`/timesheets/view/${timesheetId}`)
  }

  const handleDelete = (timesheet) => {
    setDeleteItem(timesheet)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      const timesheetId = deleteItem._id || deleteItem.id
      if (!timesheetId) {
        toast.error('Timesheet ID not found')
        return
      }
      const response = await timesheetService.deleteTimesheet(timesheetId)
      if (response.success) {
        toast.success('Timesheet deleted successfully')
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete timesheet')
      }
    } catch (error) {
      console.error('Error deleting timesheet:', error)
      if (!error._errorHandled) {
        toast.error('Failed to delete timesheet')
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const handleRevisionHistory = (timesheet) => {
    if (!timesheet) {
      toast.error('Timesheet data not found')
      return
    }
    setSelectedTimesheetData(timesheet)
    setShowRevisionHistory(true)
  }

  const handleRowSelection = (selectedRows) => {
    setSelectedRows(selectedRows)
  }

  const handleSelectionChange = (selectedRows) => {
    setSelectedRows(selectedRows)
  }

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const day = String(date.getDate()).padStart(2, '0')
    const month = months[date.getMonth()]
    const year = date.getFullYear()
    return `${day}-${month}-${year}`
  }

  // Format hours helper
  const formatHours = (totalMinutes) => {
    if (!totalMinutes) return '0h'
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60
    if (minutes === 0) {
      return `${hours}h`
    }
    return `${hours}h ${minutes}m`
  }

  // Define columns for the data table
  const columns = [
    {
      key: 'timesheetNumber',
      label: 'Timesheet #',
      sortable: true,
      render: (value) => (
        <div className="font-medium text-gray-900">
          {value}
        </div>
      )
    },
    {
      key: 'entryDate',
      label: 'Date',
      sortable: true,
      render: (value) => (
        <div className="font-medium text-gray-900">
          {formatDate(value)}
        </div>
      )
    },
    {
      key: 'employee',
      label: 'Employee',
      sortable: true,
      render: (value, row) => {
        const employee = row.employee
        if (!employee) return <span className="text-gray-500">N/A</span>
        const employeeName = typeof employee === 'string' 
          ? employee 
          : (employee.employeeName || employee.name || 'N/A')
        return (
          <div className="font-medium text-gray-900">
            {employeeName}
          </div>
        )
      }
    },
    {
      key: 'project',
      label: 'Project',
      sortable: true,
      render: (value, row) => {
        const project = row.project
        if (!project) return <span className="text-gray-500">N/A</span>
        const projectName = typeof project === 'string' 
          ? project 
          : (project.projectName || project.name || 'N/A')
        return (
          <div className="font-medium text-gray-900">
            {projectName}
          </div>
        )
      }
    },
    {
      key: 'task',
      label: 'Task',
      sortable: true,
      render: (value, row) => {
        const task = row.task
        if (!task) return <span className="text-gray-500">N/A</span>
        const taskName = typeof task === 'string' 
          ? task 
          : (task.taskName || task.name || 'N/A')
        return (
          <div className="font-medium text-gray-900">
            {taskName}
          </div>
        )
      }
    },
    {
      key: 'totalMinutes',
      label: 'Hours',
      sortable: true,
      render: (value) => (
        <div className="font-medium text-gray-900">
          {formatHours(value)}
        </div>
      )
    },
    {
      key: 'activityType',
      label: 'Activity',
      sortable: true,
      render: (value) => (
        <div className="font-medium text-gray-900">
          {value || 'N/A'}
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value) => {
        const statusColors = {
          'Draft': '#6B7280',
          'Submitted': '#3B82F6',
          'Approved': '#10B981',
          'Rejected': '#EF4444',
          'Invoiced': '#8B5CF6'
        }
        const statusColor = statusColors[value] || '#6B7280'
        return (
          <span 
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
            style={{ 
              backgroundColor: `${statusColor}20`,
              color: statusColor,
              border: `1px solid ${statusColor}40`
            }}
          >
            {value || 'N/A'}
          </span>
        )
      }
    },
    {
      key: 'isBillable',
      label: 'Billable',
      sortable: true,
      render: (value) => (
        <span 
          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
          style={{ 
            backgroundColor: value ? '#10B98120' : '#6B728020',
            color: value ? '#10B981' : '#6B7280',
            border: `1px solid ${value ? '#10B98140' : '#6B728040'}`
          }}
        >
          {value ? 'Yes' : 'No'}
        </span>
      )
    }
  ]

  const breadcrumbItems = [
    { label: 'Timesheets', path: '/timesheets', onClick: () => navigate('/timesheets') },
    { label: 'Timesheet List', path: '/timesheets/list', isCurrent: true }
  ]

  return (
    <div className="sales-dashboard">
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="page-header">
        <div className="page-title">
          <h1>Timesheet List</h1>
          <p>View and manage all timesheet entries</p>
        </div>
        <div className="page-actions">
          <button className="btn-primary" onClick={handleAddNew}>
            <Plus size={16} />
            Add New Timesheet
          </button>
        </div>
      </div>

      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <Clock size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+12%</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalTimesheets || 0}</div>
          <div className="metric-title">Total Timesheets</div>
          <div className="metric-subtitle">All entries</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <Calendar size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+8%</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalHours?.toFixed(1) || 0}</div>
          <div className="metric-title">Total Hours</div>
          <div className="metric-subtitle">Logged this month</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+3%</span>
            </div>
          </div>
          <div className="metric-value">{stats.approved || 0}</div>
          <div className="metric-title">Approved</div>
          <div className="metric-subtitle">This month</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <AlertCircle size={16} />
            </div>
            <div className="metric-trend negative">
              <TrendingUp size={12} />
              <span>+15%</span>
            </div>
          </div>
          <div className="metric-value">{stats.submitted || 0}</div>
          <div className="metric-title">Pending Approval</div>
          <div className="metric-subtitle">Awaiting review</div>
        </div>
      </div>

      <div className="dashboard-content">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          onRevisionHistory={handleRevisionHistory}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          searchPlaceholder="Search timesheets..."
          emptyMessage="No timesheets found. Create your first timesheet to get started!"
          noCard={true}
        />
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Timesheet"
        message={`Are you sure you want to delete timesheet "${deleteItem?.timesheetNumber}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      <RevisionHistoryModal
        isOpen={showRevisionHistory}
        onClose={() => setShowRevisionHistory(false)}
        data={selectedTimesheetData}
        title="Timesheet Revision History"
      />
    </div>
  )
}

export default TimesheetList

